import { Node, Edge } from "@xyflow/react";
import { Canvas, ZSLFromTemplate } from "../types";
import { extractAssetId } from "./extractAssetId";
import { uuid } from "./uuid";

export function assignAssetsAndAttributionsToCanvas(
  dehydratedCanvas: Canvas,
  zslJson: ZSLFromTemplate,
  prompt: string,
) {
  const zslData = zslJson.data;
  if (!zslData || !zslData.resourceMap) {
    return dehydratedCanvas;
  }

  try {
    // Mapping of old node IDs to new UUIDs
    const nodeIdMap: Record<string, string> = {};

    // Process nodes using reduce to avoid mutation
    const newNodes = dehydratedCanvas.nodes.reduce(
      (acc: Node[], node: Node<any>) => {
        // Exclude zap steps since they're ephemeral and are create on demand whenever we have a zap node with zap id
        if (node.type === "zapStep") return acc;

        const realId = uuid();
        nodeIdMap[node.id] = realId;

        // Create a deep copy of the node and its data
        const newNode = {
          ...node,
          id: realId,
          data: {
            ...node.data,
            attributionSourceType: prompt ? "AI_SYSTEMS" : "TEMPLATE",
          },
        };

        // Replace real asset IDs in node.data with templated strings from resourceMap
        if (node.data.tableId) {
          const tableId = extractAssetId(node.data.tableId);

          newNode.data.tableId = zslData.resourceMap[tableId];
        } else if (newNode.data.zapId) {
          const zapId = extractAssetId(node.data.zapId);

          newNode.data.zapId = zslData.resourceMap[zapId];
        } else if (node.data.pageId && node.data.projectId) {
          const projectId = extractAssetId(node.data.projectId);

          newNode.data.projectId = zslData.resourceMap[projectId];

          const pageId = extractAssetId(node.data.pageId);

          newNode.data.pageId = zslData.resourceMap[`${projectId}.${pageId}`];
        } else if (node.data.chatbotId) {
          const chatbotId = extractAssetId(node.data.chatbotId);

          newNode.data.chatbotId = zslData.resourceMap[chatbotId];
        } else if (node.data.agentId) {
          const agentId = extractAssetId(node.data.agentId);

          newNode.data.agentId = zslData.resourceMap[agentId];
        }

        acc.push(newNode);
        return acc;
      },
      [],
    );

    // Filter out zapStep edges since they're ephemeral and are create on demand whenever we have a zap node with zap id
    const newEdges = dehydratedCanvas.edges.reduce(
      (acc: Edge[], edge: Edge) => {
        if (edge.type === "zapStepEdge") return acc; // Skip ephemeral edges

        acc.push({
          ...edge,
          id: uuid(),
          source: nodeIdMap[edge.source] || edge.source, // Map to real node id
          target: nodeIdMap[edge.target] || edge.target, // Map to real node id
        });

        return acc;
      },
      [],
    );

    return {
      ...dehydratedCanvas,
      nodes: newNodes,
      edges: newEdges,
    };
  } catch (e) {
    console.error("Error assigning assets to canvas", e);
    return dehydratedCanvas;
  }
}
